import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres')
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  isAdminLogin?: boolean;
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema)
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      const user = await login(data.email, data.password);

      if (user.role === 'consultant') {
        navigate('/consultant/dashboard');
        toast.success('Bem-vindo ao painel do consultor!');
      } else if (user.is_admin) {
        navigate('/admin');
        toast.success('Bem-vindo, administrador!');
      } else {
        toast.success('Bem-vindo de volta!');
      }

      onSuccess?.();
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error instanceof Error ? error.message : 'Email ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Input
        type="email"
        label="Email"
        icon={<Mail className="w-4 h-4" />}
        error={errors.email?.message}
        {...register('email')}
        placeholder="seu@email.com"
        disabled={loading}
      />

      <Input
        type="password"
        label="Senha"
        icon={<Lock className="w-4 h-4" />}
        error={errors.password?.message}
        {...register('password')}
        placeholder="••••••••"
        disabled={loading}
      />

      <Button
        type="submit"
        loading={loading}
        className="w-full"
      >
        Acessar Portal
      </Button>

      <p className="text-sm text-purple-200/70 text-center">
        Acesso exclusivo ao portal de membros certificados.
      </p>
    </form>
  );
};